/**
 * Organisation form (labs and buyers). uc-core src/modules/org/dto: CreateOrgDto { name ≤120,
 * kind, code /^[A-Z0-9]{2,12}$/, accreditationNo? ≤40, accreditationValidTo? (date) } and
 * UpdateOrgDto { name?, accreditationNo?, accreditationValidTo? } — code and kind never change.
 * The DTO has no address, contact or GSTIN fields.
 */

/** Kinds the office sets up here; OWN / PARTNER / PROCESSOR rows are listed but made by engineers. */
export const SETUP_ORG_KINDS = ['LAB', 'BUYER'] as const
export type SetupOrgKind = (typeof SETUP_ORG_KINDS)[number]

export interface OrgDraft {
  kind: SetupOrgKind
  code: string
  name: string
  accreditationNo: string
  /** YYYY-MM-DD or '' */
  accreditationValidTo: string
}

export interface OrgProblems {
  code?: string
  name?: string
  accreditationNo?: string
  accreditationValidTo?: string
}

const CODE_RE = /^[A-Z0-9]{2,12}$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export const emptyOrgDraft = (kind: SetupOrgKind = 'LAB'): OrgDraft => ({
  kind,
  code: '',
  name: '',
  accreditationNo: '',
  accreditationValidTo: '',
})

/** Codes are capital letters and digits only (no dashes): normalise as typed. */
export const normaliseOrgCode = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12)

export function draftFromOrg(o: {
  kind: string
  code: string | null
  name: string
  accreditationNo: string | null
  accreditationValidTo: string | null
}): OrgDraft {
  return {
    kind: o.kind === 'BUYER' ? 'BUYER' : 'LAB',
    code: o.code ?? '',
    name: o.name,
    accreditationNo: o.accreditationNo ?? '',
    accreditationValidTo: o.accreditationValidTo?.slice(0, 10) ?? '',
  }
}

/**
 * `previousValidTo`: on edit, the date already stored. uc-core cannot clear it (an empty date
 * fails its IsDateString), so emptying a stored date is refused here instead of silently ignored.
 */
export function validateOrgDraft(d: OrgDraft, mode: 'create' | 'edit', previousValidTo?: string | null): OrgProblems {
  const p: OrgProblems = {}
  if (mode === 'create') {
    if (!d.code) p.code = 'Enter a short code'
    else if (!CODE_RE.test(d.code)) p.code = '2–12 capital letters or digits'
  }
  const name = d.name.trim()
  if (!name) p.name = 'Enter the name'
  else if (name.length > 120) p.name = 'At most 120 characters'
  if (d.kind === 'LAB') {
    if (d.accreditationNo.trim().length > 40) p.accreditationNo = 'At most 40 characters'
    const v = d.accreditationValidTo.trim()
    if (v && (!DATE_RE.test(v) || Number.isNaN(Date.parse(v)))) p.accreditationValidTo = 'Pick a date'
    else if (!v && mode === 'edit' && previousValidTo)
      p.accreditationValidTo = 'The date cannot be removed, only changed'
  }
  return p
}

export const orgOk = (p: OrgProblems) => !p.code && !p.name && !p.accreditationNo && !p.accreditationValidTo

/** POST /orgs body. Call only when valid. Accreditation is sent for labs only. */
export function createOrgBody(d: OrgDraft) {
  const body: {
    kind: SetupOrgKind
    code: string
    name: string
    accreditationNo?: string
    accreditationValidTo?: string
  } = { kind: d.kind, code: d.code, name: d.name.trim() }
  if (d.kind === 'LAB') {
    if (d.accreditationNo.trim()) body.accreditationNo = d.accreditationNo.trim()
    if (d.accreditationValidTo.trim()) body.accreditationValidTo = d.accreditationValidTo.trim()
  }
  return body
}

/** PATCH /orgs/:id body (code and kind cannot change). Call only when valid. */
export function updateOrgBody(d: OrgDraft) {
  const body: { name: string; accreditationNo?: string; accreditationValidTo?: string } = { name: d.name.trim() }
  if (d.kind === 'LAB') {
    // An empty number is sent as '' so a wrong number can be cleared.
    body.accreditationNo = d.accreditationNo.trim()
    if (d.accreditationValidTo.trim()) body.accreditationValidTo = d.accreditationValidTo.trim()
  }
  return body
}

export type AccreditationState = 'NONE' | 'VALID' | 'EXPIRING' | 'EXPIRED'

/** Accreditation state of a lab on `today` (YYYY-MM-DD); "expiring" = within 60 days. */
export function accreditationState(validTo: string | null | undefined, today: string): AccreditationState {
  if (!validTo) return 'NONE'
  const v = validTo.slice(0, 10)
  if (v < today) return 'EXPIRED'
  const soon = new Date(`${today}T00:00:00Z`)
  soon.setUTCDate(soon.getUTCDate() + 60)
  return v <= soon.toISOString().slice(0, 10) ? 'EXPIRING' : 'VALID'
}
